import log from "loglevel";

// A browser that blocks this site's storage (Safari or Chrome with cookies blocked for the
// site, a sandboxed iframe) throws SecurityError from the `window.localStorage` and
// `window.sessionStorage` getters themselves, before any getItem/setItem. The app reads
// storage unguarded in dozens of places, several of them on every page (the session in
// App's first render, feature flags in the Navbar, the support chat's device id). Measured
// against alpha 2026-10-27 with both getters throwing: on master the app never started --
// the server-rendered page stayed on screen with no search box and nothing to click doing
// anything -- and with the error boundaries every page was "Something went wrong".
//
// installMemoryStorageIfBlocked, run first thing in gatsby-browser.js's onClientEntry,
// replaces a blocked storage with an in-memory one, so every one of those reads works.
// What is stored lasts until the page unloads: those visitors stay logged out across full
// page loads, which their browser would have enforced anyway by blocking the cookie.
//
// Chrome defines both properties on the window itself and as configurable, so they can be
// redefined; the bare `localStorage` identifier resolves to the replacement as well
// (checked against a sandboxed iframe, where the getter throws for real).

const IN_MEMORY = Symbol("in-memory storage");

const memoryStorage = () => {
  const items = new Map();
  return {
    [IN_MEMORY]: true,
    get length() {
      return items.size;
    },
    key: (index) => Array.from(items.keys())[index] ?? null,
    getItem: (key) => (items.has(String(key)) ? items.get(String(key)) : null),
    setItem: (key, value) => {
      items.set(String(key), String(value));
    },
    removeItem: (key) => {
      items.delete(String(key));
    },
    clear: () => items.clear(),
  };
};

// True for the stand-in. Anything that relies on storage surviving a reload must check
// this: ErrorBoundary's reload-once guard, which would otherwise reload forever.
export const isInMemoryStorage = (storage) => Boolean(storage && storage[IN_MEMORY]);

const isUsable = (name) => {
  try {
    const storage = window[name];
    const probe = "__storage_probe__";
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    return true;
  } catch {
    // The getter threw, the storage is null (Firefox with dom.storage disabled), or a
    // write failed.
    return false;
  }
};

// Returns the names of the storages it replaced.
export const installMemoryStorageIfBlocked = () => {
  const replaced = [];
  for (const name of ["localStorage", "sessionStorage"]) {
    if (isUsable(name)) continue;
    try {
      Object.defineProperty(window, name, {
        configurable: true,
        enumerable: true,
        value: memoryStorage(),
      });
      replaced.push(name);
    } catch (err) {
      log.warn(`blockedStorage: could not replace ${name}`, err);
    }
  }
  if (replaced.length > 0) {
    log.info(`blockedStorage: ${replaced.join(" and ")} blocked by the browser; using an in-memory stand-in for this page.`);
  }
  return replaced;
};
